import React, { useCallback, useMemo, useState } from 'react';
import AddIcon from '@mui/icons-material/Add';
import { Box } from '@mui/material';
import Button from '@mui/material/Button';
import Tooltip from '@mui/material/Tooltip';
import moment from 'moment/moment';

import { WhiteHeaderMUIDataGrid } from 'components/ThemedMUIDataGrid/WhiteHeaderMUIDataGrid';

import FinBudgetExpenseFixedApi from '../../../../../api/finance/FinBudgetExpenseFixedApi';
import { DD_SLASH_MMM_SLASH_YYYY } from '../../../../../constants/dateFormat';
import { toBill } from '../../../../../services/financial';
import { onRequestError } from '../../../../../services/handlers';
import { muiDataGridLocaleText } from '../../../../../services/i18n/mui';
import { Amount } from '../../../finance/shared/amount-view/Amount/Amount';
import RightPanel from '../../../setup/shared/ElementSetupNew/TableSetup/TableSettings/EditTableLabel/RightPanel';
import { ADD_EXPENSE_VARIABLE, SHOW_EXPENSE_VARIABLE } from '../ExpenseFixedConstants';

import { AddExpenseVariable } from './AddExpenseVariable/AddExpenseVariable';
import { VariableExpenseDetails } from './VariableExpenseDetails/VariableExpenseDetails';

export const ExpenseVariableList = ({
  expensesVariable,
  isUserHaveAccessToManageExpenses,
  updateAvailableExpensesVariable,
  selectedSSU,
  showRightPanel,
  setShowRightPanel
}) => {
  const [newExpenseVariableData, setNewExpenseVariableData] = useState(null);
  const [updatedExpenseVariableData, setUpdatedExpenseVariableData] = useState(null);
  const [selectedVariableExpenseId, setSelectedVariableExpenseId] = useState(null);
  const [selectedVariableExpenses, setSelectedVariableExpenses] = useState([]);
  const [isAmountValid, setIsAmountValid] = useState(true);
  const [isExpenseWasUpdated, setIsExpenseWasUpdated] = useState(true);

  const closeRightPanel = useCallback(() => {
    setShowRightPanel(null);
    setNewExpenseVariableData(null);
    setUpdatedExpenseVariableData(null);
    setSelectedVariableExpenseId(null);
    setSelectedVariableExpenses([]);
  }, [setShowRightPanel]);

  const saveNewExpenseVariable = useCallback(() => {
    FinBudgetExpenseFixedApi.createExpenseVariable(selectedSSU.ssuIdentifier, {
      studyId: selectedSSU.studyIdentifier,
      siteId: selectedSSU.siteIdentifier,
      ...newExpenseVariableData
    }).then(() => {
      setShowRightPanel(null);
      updateAvailableExpensesVariable();
    }, onRequestError);
  }, [
    newExpenseVariableData,
    selectedSSU.siteIdentifier,
    selectedSSU.ssuIdentifier,
    selectedSSU.studyIdentifier,
    setShowRightPanel,
    updateAvailableExpensesVariable
  ]);

  const isFormValid = useMemo(() => {
    return !!(
      newExpenseVariableData?.date &&
      newExpenseVariableData?.finTriggerId &&
      newExpenseVariableData?.expenseAmount &&
      newExpenseVariableData?.expenseAmount > 0 &&
      isAmountValid
    );
  }, [isAmountValid, newExpenseVariableData]);

  const columns = useMemo(() => {
    const columns = [
      {
        field: 'eventDate',
        headerName: 'Event Date',
        minWidth: 140,
        type: 'date',
        valueGetter: (value, row) => value && new Date(value),
        renderCell: ({ row: { eventDate } }) => (
          <div>{eventDate ? moment(eventDate).format(DD_SLASH_MMM_SLASH_YYYY) : '—'}</div>
        )
      },
      {
        field: 'revenueDate',
        headerName: 'Revenue Date',
        minWidth: 160,
        type: 'date',
        valueGetter: (value, row) => value && new Date(value),
        renderCell: ({ row: { revenueDate } }) => (
          <div>{revenueDate ? moment(revenueDate).format(DD_SLASH_MMM_SLASH_YYYY) : 'N/A'}</div>
        )
      },
      {
        field: 'eventName',
        headerName: 'Variable Expenses - Event Name',
        flex: 1,
        minWidth: 280
      },
      {
        field: 'eventNumber',
        headerName: 'Event ID',
        flex: 1,
        minWidth: 120,
        valueGetter(value, row) {
          if (row?.adjustmentSequence) {
            return `${value}.${row.adjustmentSequence}`;
          } else {
            return value;
          }
        }
      },
      {
        field: 'clientAmount',
        headerName: 'Applied Amount',
        flex: 1,
        minWidth: 175,
        type: 'number',
        valueGetter: (value, row) => toBill(value),
        renderCell: ({ row }) => <Amount coinsAmount={row.clientAmount} showDollarSign />
      },
      {
        field: 'maxAmount',
        headerName: 'Maximum Amount',
        flex: 1,
        minWidth: 190,
        type: 'number',
        valueGetter: (value, row) => toBill(value),
        renderCell: ({ row: { maxAmount } }) =>
          maxAmount === 0 ? 'N/A' : <Amount coinsAmount={maxAmount} showDollarSign />
      },
      {
        field: 'eventStatus',
        headerName: 'Status',
        flex: 1,
        minWidth: 110
      },
      {
        field: 'addedBy',
        headerName: 'Added By',
        flex: 1,
        minWidth: 130
      },
      {
        field: 'comments',
        headerName: 'Comments',
        flex: 1,
        minWidth: 400,
        valueGetter: (value, row) => row.comments[0]?.comment,
        renderCell: ({ value }) => (
          <Tooltip title={value} placement="top" arrow>
            <div className="overflow-ellipsis">{value}</div>
          </Tooltip>
        )
      }
    ];

    if (!isUserHaveAccessToManageExpenses) {
      return columns.filter(column => !['clientAmount', 'maxAmount'].includes(column.field));
    }
    return columns;
  }, [isUserHaveAccessToManageExpenses]);

  const updateExpenseVariable = useCallback(() => {
    FinBudgetExpenseFixedApi.updateExpenseVariable(
      selectedSSU?.ssuIdentifier,
      selectedVariableExpenseId,
      updatedExpenseVariableData
    ).then(() => {
      closeRightPanel();
      updateAvailableExpensesVariable();
    }, onRequestError);
  }, [
    closeRightPanel,
    selectedSSU?.ssuIdentifier,
    selectedVariableExpenseId,
    updateAvailableExpensesVariable,
    updatedExpenseVariableData
  ]);

  return (
    <div>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'flex-end'
        }}
      >
        {isUserHaveAccessToManageExpenses && (
          <Button
            variant="contained"
            size="small"
            startIcon={<AddIcon />}
            sx={{ textTransform: 'none', lineHeight: 'unset' }}
            onClick={() => setShowRightPanel(ADD_EXPENSE_VARIABLE)}
            data-testid="add-variable-expense"
          >
            Variable Expense
          </Button>
        )}
      </Box>
      <div>
        <WhiteHeaderMUIDataGrid
          sx={{
            height: '500px',
            border: 'none'
          }}
          columns={columns}
          rows={expensesVariable}
          getRowId={row => row?.ledgerEventId}
          rowHeight={38}
          localeText={muiDataGridLocaleText({ noRowsLabel: 'No Record Found' })}
          onRowClick={({ row }) => {
            const allRowsWithSameId = expensesVariable.filter(
              expense => expense.expenseVariableId === row.expenseVariableId
            );
            setShowRightPanel(SHOW_EXPENSE_VARIABLE);
            setSelectedVariableExpenseId(row.expenseVariableId);
            setSelectedVariableExpenses(allRowsWithSameId);
          }}
          disableRowSelectionOnClick
          disableRowGrouping
          disableAggregation
          disablePivoting
          showToolbar
          slotProps={{
            root: { 'data-testid': 'expenses-variable-table' },
            toolbar: {
              printOptions: { disableToolbarButton: true },
              excelOptions: { disableToolbarButton: true },
              csvOptions: { disableToolbarButton: true }
            }
          }}
        />
      </div>
      {showRightPanel === ADD_EXPENSE_VARIABLE && (
        <RightPanel
          header="Add Expense - Variable"
          toggleClose={closeRightPanel}
          onSave={saveNewExpenseVariable}
          hideFooter={!isUserHaveAccessToManageExpenses}
          disableSaveButton={!isFormValid}
          saveButtonText="Add"
        >
          <AddExpenseVariable
            setNewExpenseVariableData={setNewExpenseVariableData}
            selectedSSU={selectedSSU}
            setIsAmountValid={setIsAmountValid}
          />
        </RightPanel>
      )}
      {showRightPanel === SHOW_EXPENSE_VARIABLE && (
        <RightPanel
          header="Variable Expense Details"
          toggleClose={closeRightPanel}
          hideFooter={!isUserHaveAccessToManageExpenses}
          disableSaveButton={!isExpenseWasUpdated}
          onSave={updateExpenseVariable}
        >
          <VariableExpenseDetails
            setUpdatedExpenseVariableData={setUpdatedExpenseVariableData}
            setIsExpenseWasUpdated={setIsExpenseWasUpdated}
            selectedVariableExpenseId={selectedVariableExpenseId}
            selectedVariableExpenses={selectedVariableExpenses}
            selectedSSU={selectedSSU}
            isUserHaveAccessToManageExpenses={isUserHaveAccessToManageExpenses}
          />
        </RightPanel>
      )}
    </div>
  );
};
